import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';

import DataTableUtils from '../../utilities/DataTableUtils';
import { ApplicationService } from '../../service/ApplicationService';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import JobViewDialog from '../../pages/competition/JobViewDialog';

const ApplicationJobListComponent = (props) => {
    const [application, setApplication] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [viewDialogVisible, setViewDialogVisible] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);
    const applicationService = useMemo(() => new ApplicationService(), []);

    useEffect(() => {
        setIsLoading(true);
        // console.log(props.application);
        setApplication(props.application);

        const getCompetitionJobs = async () => {
            await competitionService.getCompetitionJobs(props?.application.competition.id)
                .then(async (_jobs) => {
                    // console.log("Competition Jobs: ", _jobs);
                    setJobs(_jobs);
                    await getJobs(_jobs);
                })
                .catch((error) => toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getCompetitionJobs();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, props]);

    const getJobs = async (_jobs = jobs) => {
        await applicationService.getApplicationJobs(props?.application.id)
            .then((_appJobs) => {
                // console.log("Application Jobs: ", _appJobs);
                let _selectedJobs = []
                if (_appJobs.length > 0) {
                    _appJobs.forEach(item => {
                        let job = _jobs.find(j => j.id === item.competitionJob.id);
                        if (job) {
                            _selectedJobs.push(job);
                        }
                    })

                    setSelectedJobs(_selectedJobs)
                }

                setIsLoading(false)
            })
            .catch((error) => toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const onRowSelect = (data) => {
        if (application.status === 'APPLIED' || application.status === 'REGISTERED') {
            if (application.status === 'APPLIED' && data && data.length === 0) {
                toast?.current?.show({ severity: 'error', summary: translatedMessage("application.validation.jobs"), life: 5000 })
            } else {
                setSelectedJobs(data)
                updateJobs(data);
            }
        }
    }

    const updateJobs = (_jobs) => {
        applicationService.updateApplicationJobs(application.id, _jobs)
            .then(() => {
                toast?.current?.show({ severity: 'success', summary: translatedMessage("generic.selection.save.success") })
            })
            .catch(error => {
                toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
            });
    }

    const showViewDialog = (_job) => {
        setSelectedJob(_job);
        setViewDialogVisible(true);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-primary m-1"
                    onClick={() => showViewDialog(rowData)}
                    tooltip={translatedMessage('generic.edit')}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <div className='col-12 mt-3 mb-3'>
                <h5>{translatedMessage('application.job.paragraphTitle')}</h5>
                {props?.showWarning &&
                    <Message severity="warn" text={translatedMessage("application.validation.jobs")} />
                }
                <DataTable
                    ref={dt}
                    value={jobs}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    sortField="jobID"
                    sortOrder={1}
                    selectionMode="checkbox"
                    selection={selectedJobs}
                    onSelectionChange={e => onRowSelect(e.value)}
                    selectionPageOnly={true}
                    loading={isLoading}
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '1%', minWidth: '3rem' }} />
                    <Column
                        field="institution.name"
                        header={translatedMessage('institution.institution')}
                        sortable
                        headerStyle={{ width: '15%', minWidth: '10rem' }}
                    />
                    <Column
                        field="departmentType"
                        header={translatedMessage('job.departmentType')}
                        sortable
                        headerStyle={{ width: '15%', minWidth: '10rem' }}
                    />
                    <Column
                        field="departmentName"
                        header={translatedMessage('job.departmentName')}
                        sortable
                        headerStyle={{ width: '15%', minWidth: '10rem' }}
                    />
                    <Column
                        field="position"
                        header={translatedMessage('job.position')}
                        sortable
                        headerStyle={{ width: '15%', minWidth: '10rem' }}
                    />
                    <Column
                        field="jobClass"
                        header={translatedMessage('job.jobClass')}
                        sortable
                        headerStyle={{ width: '10%', minWidth: '10rem' }}
                    />
                    <Column
                        field="level"
                        header={translatedMessage('job.level')}
                        sortable
                        headerStyle={{ width: '10%', minWidth: '10rem' }}
                    />
                    <Column
                        field="jobID"
                        header={translatedMessage('job.jobID')}
                        sortable
                        headerStyle={{ width: '10%', minWidth: '10rem' }}
                    />
                    <Column body={actionBodyTemplate} />
                </DataTable>
            </div>

            {viewDialogVisible &&
                <JobViewDialog
                    value={selectedJob}
                    visible={viewDialogVisible}
                    visibleSetter={() => setViewDialogVisible(false)}
                />
            }
        </>
    );
};

export default ApplicationJobListComponent;
